import { useMemo } from 'react';
import { InvoiceRecord } from '../types';
import { safeIdComparison } from '../utils/idComparison';

/**
 * Custom hook for managing invoice assignment logic
 * 
 * This hook encapsulates the logic for determining if a user was previously
 * assigned to an invoice, which is used for separation of duties enforcement.
 * 
 * @param currentUserId - The current user's ID
 * @param invoice - The invoice record containing assignment information
 * @returns Object containing assignment-related boolean flags
 * 
 * @example
 * ```typescript
 * const { wasUserPreviouslyAssigned, isCurrentlyAssigned } = useAssignmentLogic(
 *   currentUserId,
 *   invoice
 * );
 * ```
 */
export const useAssignmentLogic = (
  currentUserId: string | undefined,
  invoice: InvoiceRecord
) => {
  // Calculate if user was previously assigned to this invoice (memoized for performance)
  const wasUserPreviouslyAssigned = useMemo(() => {
    return safeIdComparison(currentUserId, invoice.previous_assignee);
  }, [currentUserId, invoice.previous_assignee]);

  // Calculate if user is currently assigned to this invoice
  const isCurrentlyAssigned = useMemo(() => {
    return safeIdComparison(currentUserId, invoice.assigned_to);
  }, [currentUserId, invoice.assigned_to]);

  // Calculate if assignment is required for the current status
  const isAssignmentRequired = useMemo(() => {
    const assignmentRequiredStatuses = [
      'adding',
      'ir', 
      'invoicing',
      'entering_stock'
    ];
    
    return assignmentRequiredStatuses.includes(invoice.status?.toLowerCase() || '');
  }, [invoice.status]);

  return {
    wasUserPreviouslyAssigned,
    isCurrentlyAssigned,
    isAssignmentRequired,
  };
};
